import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function CostToCompletion() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  const load = async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get("/reports/cost-to-completion", { params: { project_id: pid } });
      setData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      const response = await api.post("/reports/export", { report_key: "cost_to_completion", format, project_id: projectId }, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cost_to_completion.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="cost-to-completion-page">
      <PageHeader
        title="Cost to Completion"
        description="Budget, actual, committed, estimate-to-complete and forecast per WBS item."
        action={
          <div className="flex items-center gap-2">
            <Select value={projectId} onValueChange={(v) => { setProjectId(v); load(v); }}>
              <SelectTrigger className="w-56" data-testid="ctc-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {data && (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => exportReport("excel")} data-testid="ctc-export-excel-button"><Download className="h-3.5 w-3.5" /> Excel</Button>
                <Button size="sm" variant="outline" onClick={() => exportReport("pdf")} data-testid="ctc-export-pdf-button"><Download className="h-3.5 w-3.5" /> PDF</Button>
              </div>
            )}
          </div>
        }
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data ? (
        <EmptyState title="Select a project" testId="ctc-empty-state" />
      ) : data.rows.length === 0 ? (
        <EmptyState title="No budgeted WBS items yet" testId="ctc-no-rows" />
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="text-right">Budget</TableHead><TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Committed</TableHead><TableHead className="text-right">ETC</TableHead><TableHead className="text-right">Forecast</TableHead><TableHead className="text-right">Variance</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.wbs_item_id} data-testid={`ctc-row-${r.code}`}>
                  <TableCell className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell className="text-foreground">{r.name}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.budget, 2)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.actual, 2)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.committed, 2)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtNum(r.etc, 2)}
                    {r.etc_note && <span className="ml-1 text-[10px] text-muted-foreground">({r.etc_note})</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.forecast, 2)}</TableCell>
                  <TableCell className={`text-right font-mono ${Number(r.variance) < 0 ? "text-rose-500" : "text-emerald-500"}`}>{fmtNum(r.variance, 2)}</TableCell>
                  <TableCell><EtcOverrideDialog projectId={projectId} row={r} onSaved={() => load(projectId)} /></TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(data.totals.budget, 2)}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(data.totals.actual, 2)}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(data.totals.committed, 2)}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(data.totals.etc, 2)}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(data.totals.forecast, 2)}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(data.totals.variance, 2)}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function EtcOverrideDialog({ projectId, row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [etcAmount, setEtcAmount] = useState(row.etc);
  const [note, setNote] = useState(row.etc_note || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/reports/cost-to-completion/override?project_id=${projectId}`, { wbs_item_id: row.wbs_item_id, etc_amount: etcAmount, note: note || null });
      toast.success("ETC updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`edit-etc-button-${row.code}`}><Pencil className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Edit ETC — {row.code}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="etc-override-form">
          <div><Label>Estimate to complete</Label><Input required type="number" step="0.01" value={etcAmount} onChange={(e) => setEtcAmount(e.target.value)} className="mt-1.5" data-testid="etc-amount-input" /></div>
          <div><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why does this differ from budget - actual?" className="mt-1.5" data-testid="etc-note-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="etc-override-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
